import {
    Badge,
    Banner,
    BlockStack,
    Button,
    Card,
    Checkbox,
    Collapsible,
    DataTable,
    Divider,
    InlineGrid,
    InlineStack,
    Layout,
    Modal,
    Page,
    Select,
    SkeletonBodyText,
    Tabs,
    Text,
    TextField,
    Tooltip,
} from '@shopify/polaris';
import { SafeSaveBar as SaveBar } from '../../app/components/SafeAppBridge';
import { RefreshIcon } from '@shopify/polaris-icons';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import {
    AtdAttributeMetafieldMapping,
    AtdConnectionTestResult,
    AtdConnectionTestProductSample,
    AtdCheckoutCarrierServiceStatus,
    AtdProfile,
    AtdProfileFilterOptions,
    AtdProductSyncStraggler,
    AtdProductSyncSummary,
    AtdVehicleMakeOption,
} from '../../app/models/Atd';

const defaultAtdProfile: AtdProfile = {
    name: 'Primary ATD Profile',
    standardBaseUrl: 'https://testws.atdconnect.com',
    standardApiVersion: '3.6',
    fitmentBaseUrl: 'https://testws.atdconnect.com',
    fitmentApiVersion: '3.6',
    shipToHomeBaseUrl: 'https://sthuat.atdconnect.com',
    shipToHomeApiVersion: '1.1',
    clientId: '',
    username: '',
    password: '',
    secretKey: '',
    accountNumber: '',
    locationNumber: '',
    useShipToHomeCatalog: false,
    isActive: false,
    enableAtdPull: false,
    enableAtdPushToShopify: false,
    enableFitmentPull: false,
    enableFitmentPushToShopify: false,
    enablePricePull: false,
    enablePricePushToShopify: false,
    autoSubmitFulfillmentOrders: false,
    routePickupFulfillmentsToShipToStore: true,
    createProductsAsDraft: false,
    syncExistingProductDetailsFromAtd: false,
    publishProductsToOnlineStore: false,
    useBrandProductNameSizeTitleFormat: false,
    repairExistingProductsRequested: false,
    testModeEnabled: false,
    testModeCreateLimit: 25,
    sizeAttributeValues: [],
    brandNames: [],
    categoryNames: [],
    vehicleYearMakeSelections: [],
    attributeMetafieldMappings: [],
};

const defaultFilterOptions: AtdProfileFilterOptions = {
    brandNames: [],
    categoryNames: [],
    atdAttributeNames: [],
    vehicleMakeOptions: [],
};

const recommendedAttributeNames = [
    'Size',
    'Load Index',
    'Speed Rating',
    'Load Range',
    'Ply Rating',
    'Sidewall',
    'UTQG',
    'Tread Depth',
    'Warranty',
    'Rim Diameter',
    'Section Width',
    'Aspect Ratio',
    'Overall Diameter',
    'Run Flat',
    'Season',
    'Tire Type',
    'Position',
    'Construction',
    'Color',
    'Finish',
];

const metafieldTypeOptions = [
    { label: 'Text', value: 'single_line_text_field' },
    { label: 'Whole number', value: 'number_integer' },
    { label: 'Decimal number', value: 'number_decimal' },
    { label: 'Dimension', value: 'dimension' },
    { label: 'Weight', value: 'weight' },
    { label: 'True/False', value: 'boolean' },
];

const allVehiclesFilterValue = '__all_vehicles__';
const allYearsFilterYear = '__all_years__';
const vehicleYearMakeSeparator = '|';
const productSyncTabValue = 'product-sync';
const checkoutRatesHelpText = 'Shows live ATD ship-to-home rates in Shopify checkout. Availability depends on Shopify carrier-calculated shipping support for the store plan; some stores may need Shopify Support to enable it.';

const filterOptionListStyle: React.CSSProperties = {
    maxHeight: 260,
    overflowY: 'auto',
    border: '1px solid var(--p-color-border)',
    borderRadius: 'var(--p-border-radius-200)',
    padding: 'var(--p-space-200)',
};

const vehicleFilterPanelStyle: React.CSSProperties = {
    border: '1px solid var(--p-color-border)',
    borderRadius: 'var(--p-border-radius-200)',
    padding: 'var(--p-space-400)',
};

const helpIconStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: '1px solid var(--p-color-border)',
    color: 'var(--p-color-text-subdued)',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    cursor: 'help',
};

const formatDateTime = (value?: string) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-';

const formatMoney = (value?: number) => value === undefined || value === null ? '-' : `$${value.toFixed(2)}`;

const formatInteger = (value?: number) => value === undefined || value === null ? '-' : value.toString();

const normalizeKey = (value?: string) => (value || '').trim().toLowerCase();

const toYearNumber = (value?: string) => {
    const parsed = Number.parseInt(value || '', 10);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const isAllVehiclesSelection = (value?: string) =>
    normalizeKey(value) === normalizeKey(allVehiclesFilterValue);

const getErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    if (data && typeof data === 'object') return JSON.stringify(data);
    return error?.message || fallback;
};

const mergeOptions = (...groups: Array<Array<string | undefined>>) => {
    const values = new Map<string, string>();
    groups.flat().forEach(value => {
        const trimmed = value?.trim();
        if (!trimmed) return;
        const key = normalizeKey(trimmed);
        if (!values.has(key)) values.set(key, trimmed);
    });

    return Array.from(values.values()).sort((left, right) => left.localeCompare(right));
};

const normalizeVehicleMakeOptions = (options?: AtdVehicleMakeOption[]) => {
    const values = new Map<string, AtdVehicleMakeOption>();
    (options || []).forEach(option => {
        const value = option.value?.trim();
        const label = option.label?.trim();
        if (!value || !label) return;
        const key = normalizeKey(value);
        if (!values.has(key)) {
            values.set(key, {
                year: option.year?.trim() || '',
                make: option.make?.trim() || '',
                value,
                label,
            });
        }
    });

    return Array.from(values.values()).sort((left, right) =>
        right.year.localeCompare(left.year) || left.make.localeCompare(right.make));
};

const buildVehicleYearMakeValue = (year: string, make: string) =>
    `${year.trim()}${vehicleYearMakeSeparator}${make.trim()}`;

const buildAllYearsMakeValue = (make: string) =>
    buildVehicleYearMakeValue(allYearsFilterYear, make);

const parseVehicleYearMakeValue = (value?: string) => {
    if (!value || isAllVehiclesSelection(value)) return undefined;

    const parts = value.split(vehicleYearMakeSeparator);
    if (parts.length !== 2) return undefined;

    const year = parts[0]?.trim();
    const make = parts[1]?.trim();
    if (!year || !make) return undefined;

    return { year: normalizeKey(year) === allYearsFilterYear ? allYearsFilterYear : year, make, value: buildVehicleYearMakeValue(year, make) };
};

interface VehicleSelectionRange {
    key: string;
    make: string;
    yearLabel: string;
    yearCountLabel: string;
    values: string[];
    sortYear: number;
}

const buildSelectedVehicleRanges = (
    selectedValues: string[],
    options: AtdVehicleMakeOption[]) => {
    const optionByKey = new Map<string, AtdVehicleMakeOption>();
    options.forEach(option => optionByKey.set(normalizeKey(option.value), option));

    const groupedByMake = new Map<string, { make: string; allYearsValue?: string; years: Map<string, { year: string; value: string }> }>();

    selectedValues
        .filter(value => !isAllVehiclesSelection(value))
        .forEach(value => {
            const option = optionByKey.get(normalizeKey(value));
            const parsed = option
                ? { year: option.year, make: option.make, value: option.value }
                : parseVehicleYearMakeValue(value);

            if (!parsed) return;

            const makeKey = normalizeKey(parsed.make);
            if (!groupedByMake.has(makeKey)) {
                groupedByMake.set(makeKey, { make: parsed.make, years: new Map() });
            }

            if (parsed.year === allYearsFilterYear) {
                groupedByMake.get(makeKey)!.allYearsValue = parsed.value;
                return;
            }

            groupedByMake.get(makeKey)!.years.set(parsed.year, parsed);
        });

    const ranges: VehicleSelectionRange[] = [];
    groupedByMake.forEach(group => {
        if (group.allYearsValue) {
            ranges.push({
                key: `${normalizeKey(group.make)}:all-years`,
                make: group.make,
                yearLabel: 'All years',
                yearCountLabel: 'All',
                values: [group.allYearsValue],
                sortYear: Number.MAX_SAFE_INTEGER,
            });
            return;
        }

        const years = Array.from(group.years.values())
            .map(item => ({
                ...item,
                yearNumber: toYearNumber(item.year),
            }))
            .sort((left, right) => {
                const leftYear = left.yearNumber ?? Number.MIN_SAFE_INTEGER;
                const rightYear = right.yearNumber ?? Number.MIN_SAFE_INTEGER;
                return rightYear - leftYear || left.year.localeCompare(right.year);
            });

        let currentRange: typeof years = [];
        const flushRange = () => {
            if (currentRange.length === 0) return;

            const sortedAscending = [...currentRange].sort((left, right) => {
                const leftYear = left.yearNumber ?? Number.MIN_SAFE_INTEGER;
                const rightYear = right.yearNumber ?? Number.MIN_SAFE_INTEGER;
                return leftYear - rightYear || left.year.localeCompare(right.year);
            });
            const first = sortedAscending[0];
            const last = sortedAscending[sortedAscending.length - 1];
            const yearLabel = first.year === last.year ? first.year : `${first.year}-${last.year}`;
            const values = sortedAscending.map(item => item.value);
            const sortYear = last.yearNumber ?? Number.MIN_SAFE_INTEGER;

            ranges.push({
                key: `${normalizeKey(group.make)}:${yearLabel}`,
                make: group.make,
                yearLabel,
                yearCountLabel: values.length.toString(),
                values,
                sortYear,
            });
            currentRange = [];
        };

        years.forEach(item => {
            const previous = currentRange[currentRange.length - 1];
            const isContiguous = previous?.yearNumber !== undefined
                && item.yearNumber !== undefined
                && item.yearNumber === previous.yearNumber - 1;

            if (currentRange.length > 0 && !isContiguous) {
                flushRange();
            }

            currentRange.push(item);
        });

        flushRange();
    });

    return ranges.sort((left, right) =>
        left.make.localeCompare(right.make) || right.sortYear - left.sortYear);
};

const normalizeProfile = (profile: AtdProfile): AtdProfile => ({
    ...defaultAtdProfile,
    ...profile,
    sizeAttributeValues: profile.sizeAttributeValues || [],
    brandNames: profile.brandNames || [],
    categoryNames: profile.categoryNames || [],
    vehicleYearMakeSelections: profile.vehicleYearMakeSelections || [],
    attributeMetafieldMappings: profile.attributeMetafieldMappings || [],
});

const normalizeFilterOptions = (options: AtdProfileFilterOptions): AtdProfileFilterOptions => ({
    brandNames: mergeOptions(options.brandNames || []),
    categoryNames: mergeOptions(options.categoryNames || []),
    atdAttributeNames: mergeOptions(options.atdAttributeNames || []),
    vehicleMakeOptions: normalizeVehicleMakeOptions(options.vehicleMakeOptions),
});

const parseDelimitedValues = (value: string) =>
    value
        .split(/[\n,;]+/)
        .map(item => item.trim())
        .filter(Boolean);

const normalizeStringArrayForSnapshot = (values?: string[]) =>
    (values || [])
        .map(value => value.trim())
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right));

const profileSnapshot = (value: AtdProfile, includeRepairRequest = true) => {
    const snapshot = {
        ...value,
        repairExistingProductsRequested: includeRepairRequest ? value.repairExistingProductsRequested : false,
        sizeAttributeValues: normalizeStringArrayForSnapshot(value.sizeAttributeValues),
        brandNames: normalizeStringArrayForSnapshot(value.brandNames),
        categoryNames: normalizeStringArrayForSnapshot(value.categoryNames),
        vehicleYearMakeSelections: normalizeStringArrayForSnapshot(value.vehicleYearMakeSelections),
        attributeMetafieldMappings: (value.attributeMetafieldMappings || [])
            .map(mapping => ({
                atdAttributeName: mapping.atdAttributeName?.trim() || '',
                shopifyProductMetafield: mapping.shopifyProductMetafield?.trim() || '',
                shopifyMetafieldType: mapping.shopifyMetafieldType || 'single_line_text_field',
            }))
            .sort((left, right) => `${left.atdAttributeName}.${left.shopifyProductMetafield}`.localeCompare(`${right.atdAttributeName}.${right.shopifyProductMetafield}`)),
    };

    return JSON.stringify(snapshot);
};

interface FilterCheckboxListProps {
    title: string;
    allLabel: string;
    emptyText: string;
    options: string[];
    selectedValues: string[];
    onToggle: (value: string, checked: boolean) => void;
    onSelectAll?: () => void;
    allowEmptySelection?: boolean;
    emptySelectionLabel?: string;
    optionLabels?: Record<string, string>;
}

const FilterCheckboxList: React.FC<FilterCheckboxListProps> = ({
    title,
    allLabel,
    emptyText,
    options,
    selectedValues,
    onToggle,
    onSelectAll,
    allowEmptySelection = false,
    emptySelectionLabel = '0 selected',
    optionLabels = {},
}) => {
    const explicitSelectedValues = selectedValues.length > 0 || allowEmptySelection ? selectedValues : options;
    const selectedKeys = new Set(explicitSelectedValues.map(normalizeKey));
    const allSelected = options.length > 0 && !allowEmptySelection && selectedValues.length === 0;
    const selectedCount = allSelected ? options.length : selectedKeys.size;
    const selectionLabel = allSelected
        ? allLabel
        : selectedCount === 0
            ? emptySelectionLabel
            : `${selectedCount} selected`;

    return (
        <BlockStack gap="200">
            <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingSm">{title}</Text>
                <InlineStack gap="200" blockAlign="center">
                    <Text as="span" tone="subdued">
                        {selectionLabel}
                    </Text>
                    {!allSelected && options.length > 0 && onSelectAll ? (
                        <Button onClick={onSelectAll}>Select all</Button>
                    ) : null}
                </InlineStack>
            </InlineStack>

            {options.length === 0 ? (
                <Text as="p" tone="subdued">{emptyText}</Text>
            ) : (
                <div style={filterOptionListStyle}>
                    <BlockStack gap="100">
                        {options.map(option => {
                            const checked = selectedKeys.has(normalizeKey(option));
                            const isLastSelectedOption = !allowEmptySelection && checked && selectedKeys.size === 1;

                            return (
                                <Checkbox
                                    key={option}
                                    label={optionLabels[option] || option}
                                    checked={checked}
                                    disabled={isLastSelectedOption}
                                    onChange={(newChecked) => onToggle(option, newChecked)}
                                />
                            );
                        })}
                    </BlockStack>
                </div>
            )}
        </BlockStack>
    );
};

interface VehicleFilterBuilderProps {
    options: AtdVehicleMakeOption[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
}

const VehicleFilterBuilder: React.FC<VehicleFilterBuilderProps> = ({
    options,
    selectedValues,
    onChange,
}) => {
    const [selectedMake, setSelectedMake] = useState('');
    const [startYear, setStartYear] = useState('');
    const [endYear, setEndYear] = useState('');

    const allVehiclesEnabled = selectedValues.some(isAllVehiclesSelection);

    const makes = useMemo(() => {
        const values = new Map<string, string>();
        options.forEach(option => {
            const make = option.make?.trim();
            if (!make) return;
            const key = normalizeKey(make);
            if (!values.has(key)) values.set(key, make);
        });

        return Array.from(values.values()).sort((left, right) => left.localeCompare(right));
    }, [options]);

    const yearsByMake = useMemo(() => {
        const values = new Map<string, string[]>();

        options.forEach(option => {
            const makeKey = normalizeKey(option.make);
            const year = option.year?.trim();
            if (!makeKey || !year) return;

            const years = values.get(makeKey) || [];
            if (!years.some(existing => normalizeKey(existing) === normalizeKey(year))) {
                years.push(year);
            }
            values.set(makeKey, years);
        });

        values.forEach((years, makeKey) => {
            values.set(makeKey, years.sort((left, right) => {
                const leftYear = toYearNumber(left) ?? Number.MIN_SAFE_INTEGER;
                const rightYear = toYearNumber(right) ?? Number.MIN_SAFE_INTEGER;
                return rightYear - leftYear || right.localeCompare(left);
            }));
        });

        return values;
    }, [options]);

    const selectedMakeYears = yearsByMake.get(normalizeKey(selectedMake)) || [];
    const yearOptions = [
        { label: 'Choose year', value: '' },
        { label: 'All years', value: allYearsFilterYear },
        ...selectedMakeYears.map(year => ({ label: year, value: year })),
    ];
    const selectedRanges = useMemo(
        () => buildSelectedVehicleRanges(selectedValues, options),
        [selectedValues, options]);

    const addRange = useCallback(() => {
        if (!selectedMake) return;

        const makeKey = normalizeKey(selectedMake);
        const isAllYearsSelection = startYear === allYearsFilterYear || endYear === allYearsFilterYear;
        if (isAllYearsSelection) {
            // Store a make-level selection so future ATD years for the same make
            // are picked up automatically without the merchant revisiting this page.
            const existingValues = selectedValues.filter(value => {
                const parsed = parseVehicleYearMakeValue(value);
                return !parsed || normalizeKey(parsed.make) !== makeKey;
            });
            onChange(mergeOptions(existingValues, [buildAllYearsMakeValue(selectedMake)]));
            return;
        }

        if (!startYear || !endYear) return;

        const start = toYearNumber(startYear);
        const end = toYearNumber(endYear);
        if (start === undefined || end === undefined) return;

        const low = Math.min(start, end);
        const high = Math.max(start, end);
        const valuesToAdd = options
            .filter(option => {
                const optionYear = toYearNumber(option.year);
                return normalizeKey(option.make) === makeKey
                    && optionYear !== undefined
                    && optionYear >= low
                    && optionYear <= high;
            })
            .map(option => option.value);

        if (valuesToAdd.length === 0) return;

        const existingValues = selectedValues.filter(value => {
            if (isAllVehiclesSelection(value)) {
                return false;
            }

            const parsed = parseVehicleYearMakeValue(value);
            return !parsed || parsed.year !== allYearsFilterYear || normalizeKey(parsed.make) !== makeKey;
        });
        onChange(mergeOptions(existingValues, valuesToAdd));
    }, [endYear, onChange, options, selectedMake, selectedValues, startYear]);

    const removeRange = useCallback((values: string[]) => {
        const valueKeys = new Set(values.map(normalizeKey));
        onChange(selectedValues.filter(value => !valueKeys.has(normalizeKey(value))));
    }, [onChange, selectedValues]);

    const toggleAllVehicles = useCallback((checked: boolean) => {
        onChange(checked ? [allVehiclesFilterValue] : []);
    }, [onChange]);

    const handleMakeChange = useCallback((value: string) => {
        setSelectedMake(value);
        setStartYear('');
        setEndYear('');
    }, []);

    const handleStartYearChange = useCallback((value: string) => {
        setStartYear(value);
        if (value === allYearsFilterYear) {
            setEndYear(allYearsFilterYear);
        } else if (endYear === allYearsFilterYear) {
            setEndYear('');
        }
    }, [endYear]);

    const handleEndYearChange = useCallback((value: string) => {
        setEndYear(value);
        if (value === allYearsFilterYear) {
            setStartYear(allYearsFilterYear);
        } else if (startYear === allYearsFilterYear) {
            setStartYear('');
        }
    }, [startYear]);

    const canAddVehicleFilter = Boolean(selectedMake && (
        startYear === allYearsFilterYear
        || endYear === allYearsFilterYear
        || (startYear && endYear)
    ));

    return (
        <div style={vehicleFilterPanelStyle}>
        <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingSm">Vehicles</Text>
                <Text as="span" tone="subdued">
                    {allVehiclesEnabled
                        ? 'All vehicles'
                        : selectedRanges.length === 0
                            ? 'No vehicle filter'
                            : `${selectedRanges.length} filter${selectedRanges.length === 1 ? '' : 's'}`}
                </Text>
            </InlineStack>

            <Checkbox
                label="Enable all vehicles"
                checked={allVehiclesEnabled}
                onChange={toggleAllVehicles}
                helpText="Imports fitment for every ATD year/make. Leave off to use selected make and year ranges."
            />

            {!allVehiclesEnabled ? (
                options.length === 0 ? (
                    <Text as="p" tone="subdued">Vehicle make choices will appear after ATD fitment years and makes have synced.</Text>
                ) : (
                    <BlockStack gap="300">
                        <InlineGrid columns={{ xs: 1, md: 4 }} gap="300">
                            <Select
                                label="Make"
                                options={[
                                    { label: 'Choose make', value: '' },
                                    ...makes.map(make => ({ label: make, value: make })),
                                ]}
                                value={selectedMake}
                                onChange={handleMakeChange}
                            />
                            <Select
                                label="From year"
                                options={yearOptions}
                                value={startYear}
                                onChange={handleStartYearChange}
                                disabled={!selectedMake}
                            />
                            <Select
                                label="To year"
                                options={yearOptions}
                                value={endYear}
                                onChange={handleEndYearChange}
                                disabled={!selectedMake || startYear === allYearsFilterYear}
                            />
                            <div style={{ alignSelf: 'end' }}>
                                <Button
                                    onClick={addRange}
                                    disabled={!canAddVehicleFilter}
                                >
                                    Add
                                </Button>
                            </div>
                        </InlineGrid>

                        {selectedRanges.length === 0 ? (
                            <Text as="p" tone="subdued">No selected vehicle filters.</Text>
                        ) : (
                            <DataTable
                                columnContentTypes={['text', 'text', 'text', 'text']}
                                headings={['Make', 'Years', 'Year count', 'Action']}
                                rows={selectedRanges.map(range => [
                                    range.make,
                                    range.yearLabel,
                                    range.yearCountLabel,
                                    <Button key={range.key} tone="critical" onClick={() => removeRange(range.values)}>Remove</Button>,
                                ])}
                            />
                        )}
                    </BlockStack>
                )
            ) : null}
        </BlockStack>
        </div>
    );
};

const formatCount = (value?: number) => value === undefined || value === null ? '-' : value.toLocaleString();

const AtdProductSyncPanel: React.FC = () => {
    const [summary, setSummary] = useState<AtdProductSyncSummary | undefined>();
    const [loading, setLoading] = useState(false);
    const [cleanupLoading, setCleanupLoading] = useState(false);
    const [cleanupTarget, setCleanupTarget] = useState<{ mode: 'single' | 'listed'; product?: AtdProductSyncStraggler } | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [notice, setNotice] = useState<string | undefined>();

    const loadSummary = useCallback(async () => {
        try {
            setLoading(true);
            setError(undefined);
            setSummary(await agent.Atd.productSyncSummary());
        } catch (loadError: any) {
            setError(getErrorMessage(loadError, 'Unable to load ATD product sync details.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const listedStragglers = useMemo(() => summary?.stragglers || [], [summary?.stragglers]);
    const cleanupCount = cleanupTarget?.mode === 'listed' ? listedStragglers.length : cleanupTarget?.product ? 1 : 0;
    const hasMoreStragglers = Boolean(summary && summary.stragglerProductCount > listedStragglers.length);

    const runCleanup = useCallback(async () => {
        if (!cleanupTarget || cleanupCount === 0) return;

        // Only archive products the merchant can review in this UI. The API also
        // validates that each submitted ID is still a straggler before mutating it.
        const atdProductIds = cleanupTarget.mode === 'listed'
            ? listedStragglers.map(item => item.atdProductId)
            : cleanupTarget.product
                ? [cleanupTarget.product.atdProductId]
                : [];

        try {
            setCleanupLoading(true);
            setError(undefined);
            setNotice(undefined);
            const result = await agent.Atd.archiveProductSyncStragglers({
                archiveAll: false,
                atdProductIds,
            });
            setNotice(result.message);
            setCleanupTarget(undefined);
            await loadSummary();
        } catch (cleanupError: any) {
            setError(getErrorMessage(cleanupError, 'Unable to archive ATD straggler products.'));
        } finally {
            setCleanupLoading(false);
        }
    }, [cleanupCount, cleanupTarget, listedStragglers, loadSummary]);

    const stragglerRows = listedStragglers.map(item => [
        <BlockStack key={`product-${item.atdProductId}`} gap="050">
            <Text as="span" fontWeight="semibold">{item.shopifyTitle || item.productName || item.partNumber || `ATD product ${item.atdProductId}`}</Text>
            <Text as="span" tone="subdued">{item.brandName || '-'} / {item.partNumber || '-'}</Text>
        </BlockStack>,
        item.shopifyProductId ? item.shopifyProductId.toString() : '-',
        item.shopifyStatus || '-',
        formatDateTime(item.lastCatalogSyncAt),
        item.reasons.join(' '),
        <Button key={`archive-${item.atdProductId}`} tone="critical" onClick={() => setCleanupTarget({ mode: 'single', product: item })}>
            Archive and unlink
        </Button>,
    ]);

    const actionLogRows = (summary?.actionLogs || []).map(log => [
        formatDateTime(log.createdAt),
        log.action,
        log.status,
        `${log.succeededCount}/${log.requestedCount}`,
        log.errorMessage || '-',
    ]);

    return (
        <>
            <Modal
                open={Boolean(cleanupTarget)}
                onClose={() => {
                    if (!cleanupLoading) setCleanupTarget(undefined);
                }}
                title="Archive and unlink ATD products"
                primaryAction={{
                    content: `Archive ${cleanupCount} product${cleanupCount === 1 ? '' : 's'}`,
                    destructive: true,
                    onAction: runCleanup,
                    loading: cleanupLoading,
                    disabled: cleanupLoading || cleanupCount === 0,
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: () => setCleanupTarget(undefined),
                    disabled: cleanupLoading,
                }]}
            >
                <Modal.Section>
                    <BlockStack gap="200">
                        <Text as="p">
                            This archives the Shopify product{cleanupCount === 1 ? '' : 's'} and removes the ATD product link in PartFits.
                        </Text>
                        <Text as="p" tone="critical">
                            This action cannot be undone from PartFits. The action will be logged for audit history.
                        </Text>
                    </BlockStack>
                </Modal.Section>
            </Modal>

            <BlockStack gap="400">
                {error ? (
                    <Banner tone="critical" title="ATD product sync error">
                        <p>{error}</p>
                    </Banner>
                ) : null}

                {notice ? (
                    <Banner tone="success" title="ATD product sync updated">
                        <p>{notice}</p>
                    </Banner>
                ) : null}

                <Card>
                    <BlockStack gap="300">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text as="h2" variant="headingMd">Product sync</Text>
                            <Button onClick={loadSummary} loading={loading}>Refresh</Button>
                        </InlineStack>
                        <Text as="p" tone="subdued">
                            Connected products are ATD rows linked to Shopify products. Available products are the latest successful ATD catalog count for this profile.
                        </Text>
                        <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
                            <BlockStack gap="050">
                                <Text as="span" tone="subdued">Linked / available</Text>
                                <Text as="p" variant="headingLg">
                                    {formatCount(summary?.linkedProductCount)} / {formatCount(summary?.availableProductCount)}
                                </Text>
                            </BlockStack>
                            <BlockStack gap="050">
                                <Text as="span" tone="subdued">Straggler products</Text>
                                <Text as="p" variant="headingLg" tone={(summary?.stragglerProductCount || 0) > 0 ? 'critical' : undefined}>
                                    {formatCount(summary?.stragglerProductCount)}
                                </Text>
                            </BlockStack>
                            <BlockStack gap="050">
                                <Text as="span" tone="subdued">Latest catalog sync</Text>
                                <Text as="p">{formatDateTime(summary?.latestCatalogSyncEndedAt || summary?.latestCatalogSyncStartedAt)}</Text>
                            </BlockStack>
                        </InlineGrid>
                    </BlockStack>
                </Card>

                <Card>
                    <BlockStack gap="300">
                        <InlineStack align="space-between" blockAlign="center">
                            <Text as="h2" variant="headingMd">Straggler products</Text>
                            <Button
                                tone="critical"
                                disabled={listedStragglers.length === 0 || cleanupLoading}
                                onClick={() => setCleanupTarget({ mode: 'listed' })}
                            >
                                Archive listed products
                            </Button>
                        </InlineStack>
                        <Text as="p" tone="subdued">
                            Stragglers are ATD-linked Shopify products that are no longer returned by the latest ATD catalog sync or do not match the current product sync filters.
                        </Text>
                        {hasMoreStragglers ? (
                            <Banner tone="warning">
                                <p>Showing the first {listedStragglers.length} of {summary?.stragglerProductCount} straggler products.</p>
                            </Banner>
                        ) : null}
                        {loading ? (
                            <SkeletonBodyText lines={5} />
                        ) : stragglerRows.length > 0 ? (
                            <DataTable
                                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                                headings={['Product', 'Shopify ID', 'Status', 'Last seen in ATD', 'Reason', 'Action']}
                                rows={stragglerRows}
                            />
                        ) : (
                            <Text as="p" tone="subdued">No ATD straggler products found.</Text>
                        )}
                    </BlockStack>
                </Card>

                <Card>
                    <BlockStack gap="300">
                        <Text as="h2" variant="headingMd">Cleanup audit history</Text>
                        {actionLogRows.length > 0 ? (
                            <DataTable
                                columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                                headings={['When', 'Action', 'Status', 'Succeeded', 'Error']}
                                rows={actionLogRows}
                            />
                        ) : (
                            <Text as="p" tone="subdued">No ATD product cleanup actions have been logged yet.</Text>
                        )}
                    </BlockStack>
                </Card>
            </BlockStack>
        </>
    );
};

const AtdProfilePage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<AtdProfile>(defaultAtdProfile);
    const [savedProfile, setSavedProfile] = useState<AtdProfile>(defaultAtdProfile);
    const [filterOptions, setFilterOptions] = useState<AtdProfileFilterOptions>(defaultFilterOptions);
    const [loading, setLoading] = useState(false);
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [apiEndpointsOpen, setApiEndpointsOpen] = useState(false);
    const [testResultModalOpen, setTestResultModalOpen] = useState(false);
    const [selectedFilterTab, setSelectedFilterTab] = useState(0);
    const [selectedProfileTab, setSelectedProfileTab] = useState(() =>
        new URLSearchParams(location.search).get('tab') === productSyncTabValue ? 1 : 0);
    const [testResult, setTestResult] = useState<AtdConnectionTestResult | undefined>();
    const [checkoutRateStatus, setCheckoutRateStatus] = useState<AtdCheckoutCarrierServiceStatus | undefined>();
    const [checkoutRateLoading, setCheckoutRateLoading] = useState(false);
    const [checkoutRateSaving, setCheckoutRateSaving] = useState(false);
    const [checkoutRateError, setCheckoutRateError] = useState<string | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [notice, setNotice] = useState<string | undefined>();

    const loadCheckoutRateStatus = useCallback(async () => {
        try {
            // This checks Shopify's CarrierService state live, separate from the
            // saved ATD profile, because merchants can change shipping services
            // directly in Shopify admin.
            setCheckoutRateLoading(true);
            setCheckoutRateError(undefined);
            setCheckoutRateStatus(await agent.Atd.checkoutRatesStatus());
        } catch (statusError: any) {
            setCheckoutRateStatus(undefined);
            setCheckoutRateError(getErrorMessage(statusError, 'Unable to load Shopify checkout rate status.'));
        } finally {
            setCheckoutRateLoading(false);
        }
    }, []);

    const loadProfile = useCallback(async () => {
        try {
            setLoading(true);
            setError(undefined);
            const [loadedProfile, loadedFilterOptions] = await Promise.all([
                agent.Atd.profile(),
                agent.Atd.profileFilterOptions(),
            ]);
            const normalizedProfile = normalizeProfile(loadedProfile);
            setProfile(normalizedProfile);
            setSavedProfile(normalizedProfile);
            setFilterOptions(normalizeFilterOptions(loadedFilterOptions));
            setTestResult(undefined);
            setProfileLoaded(true);
            loadCheckoutRateStatus();
        } catch (loadError: any) {
            setProfileLoaded(false);
            setError(getErrorMessage(loadError, 'Unable to load ATD connection settings.'));
        } finally {
            setLoading(false);
        }
    }, [loadCheckoutRateStatus]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    useEffect(() => {
        setSelectedProfileTab(new URLSearchParams(location.search).get('tab') === productSyncTabValue ? 1 : 0);
    }, [location.search]);

    const handleProfileTabSelect = useCallback((index: number) => {
        setSelectedProfileTab(index);
        navigate(index === 1 ? `/integrations/atd/profile?tab=${productSyncTabValue}` : '/integrations/atd/profile');
    }, [navigate]);

    const updateProfile = useCallback(<K extends keyof AtdProfile>(key: K, value: AtdProfile[K]) => {
        setProfile(current => ({ ...current, [key]: value }));
    }, []);

    const updateSyncEnabled = useCallback((checked: boolean) => {
        setProfile(current => ({
            ...current,
            isActive: checked,
            enableAtdPull: checked,
            enableAtdPushToShopify: checked,
            enableFitmentPull: checked,
            enableFitmentPushToShopify: checked,
            // Pricing is intentionally excluded from this merchant-facing switch.
            // ATD pricing should come from a separate price feed integration when we add it.
        }));
    }, []);

    const selectAllFilterValues = useCallback((field: 'brandNames' | 'categoryNames') => {
        setProfile(current => ({ ...current, [field]: [] }));
    }, []);

    const toggleFilterValue = useCallback((field: 'brandNames' | 'categoryNames', value: string, checked: boolean, allValues: string[]) => {
        setProfile(current => {
            const currentValues = current[field] || [];
            const explicitValues = currentValues.length > 0 ? currentValues : allValues;
            const valueKey = normalizeKey(value);
            const nextValues = checked
                ? mergeOptions(explicitValues, [value])
                : explicitValues.filter(item => normalizeKey(item) !== valueKey);

            if (nextValues.length === 0 || nextValues.length === allValues.length) {
                return { ...current, [field]: [] };
            }

            return { ...current, [field]: nextValues };
        });
    }, []);

    const updateVehicleYearMakeSelections = useCallback((values: string[]) => {
        setProfile(current => ({ ...current, vehicleYearMakeSelections: values }));
    }, []);

    const repairOnlyProfileChange = useMemo(() =>
        profile.repairExistingProductsRequested &&
        profileSnapshot(profile, false) === profileSnapshot(savedProfile, false) &&
        profileSnapshot(profile) !== profileSnapshot(savedProfile),
        [profile, savedProfile]);

    const saveProfile = useCallback(async () => {
        try {
            setSaving(true);
            setError(undefined);
            setNotice(undefined);
            if (repairOnlyProfileChange) {
                const result = await agent.Atd.requestProductRepair();
                const queuedProfile = normalizeProfile({
                    ...profile,
                    repairExistingProductsRequested: true,
                });
                setProfile(queuedProfile);
                setSavedProfile(queuedProfile);
                setNotice(result.message || 'ATD product repair queued for the next product sync.');
            } else {
                const saved = await agent.Atd.saveProfile(profile);
                const normalizedProfile = normalizeProfile(saved);
                setProfile(normalizedProfile);
                setSavedProfile(normalizedProfile);
                setNotice('ATD connection settings saved.');
            }
        } catch (saveError: any) {
            setError(getErrorMessage(saveError, 'Unable to save ATD connection settings.'));
        } finally {
            setSaving(false);
        }
    }, [profile, repairOnlyProfileChange]);

    const discardChanges = useCallback(() => {
        setProfile(savedProfile);
        setError(undefined);
        setNotice(undefined);
        setTestResult(undefined);
        setTestResultModalOpen(false);
    }, [savedProfile]);

    const testProfile = useCallback(async () => {
        try {
            setTesting(true);
            setError(undefined);
            setNotice(undefined);
            setTestResult(await agent.Atd.testProfile(profile));
            setTestResultModalOpen(true);
        } catch (testError: any) {
            setError(getErrorMessage(testError, 'Unable to test ATD connection.'));
        } finally {
            setTesting(false);
        }
    }, [profile]);

    const ensureCheckoutRates = useCallback(async () => {
        try {
            // The backend owns create-vs-update decisions so this button can be
            // safely used for both first install and repair after domain changes.
            setCheckoutRateSaving(true);
            setCheckoutRateError(undefined);
            setNotice(undefined);
            const status = await agent.Atd.ensureCheckoutRates();
            setCheckoutRateStatus(status);
            setNotice(status.message || 'Shopify checkout rates configured.');
        } catch (ensureError: any) {
            setCheckoutRateError(getErrorMessage(ensureError, 'Unable to configure Shopify checkout rates.'));
        } finally {
            setCheckoutRateSaving(false);
        }
    }, []);

    const addMapping = useCallback(() => {
        setProfile(current => ({
            ...current,
            attributeMetafieldMappings: [
                ...(current.attributeMetafieldMappings || []),
                { atdAttributeName: '', shopifyProductMetafield: '', shopifyMetafieldType: 'single_line_text_field' },
            ],
        }));
    }, []);

    const updateMapping = useCallback((index: number, field: keyof AtdAttributeMetafieldMapping, value: string) => {
        setProfile(current => {
            const mappings = [...(current.attributeMetafieldMappings || [])];
            if (!mappings[index]) return current;
            mappings[index] = { ...mappings[index], [field]: value };
            return { ...current, attributeMetafieldMappings: mappings };
        });
    }, []);

    const removeMapping = useCallback((index: number) => {
        setProfile(current => ({
            ...current,
            attributeMetafieldMappings: (current.attributeMetafieldMappings || []).filter((_, mappingIndex) => mappingIndex !== index),
        }));
    }, []);

    const attributeOptions = useMemo(() => {
        const currentMappingAttributes = (profile.attributeMetafieldMappings || []).map(mapping => mapping.atdAttributeName);
        const names = mergeOptions(filterOptions.atdAttributeNames, recommendedAttributeNames, currentMappingAttributes);

        return [
            { label: 'Choose ATD attribute', value: '' },
            ...names.map(name => ({ label: name, value: name })),
        ];
    }, [filterOptions.atdAttributeNames, profile.attributeMetafieldMappings]);

    // Inventory push shares the ATD push gate in the sync worker, so this
    // merchant-facing state stays aligned with catalog/fitment/inventory.
    const syncEnabled = profile.isActive &&
        profile.enableAtdPull &&
        profile.enableAtdPushToShopify &&
        profile.enableFitmentPull &&
        profile.enableFitmentPushToShopify;

    const isConnectionConfigured = Boolean(
        profile.clientId &&
        profile.username &&
        profile.password &&
        profile.locationNumber
    );

    const hasUnsavedChanges = profileSnapshot(profile) !== profileSnapshot(savedProfile);
    const checkoutRatesReady = Boolean(
        checkoutRateStatus?.exists &&
        checkoutRateStatus.active &&
        checkoutRateStatus.callbackUrlMatches
    );

    const profileTabs = [
        {
            id: 'settings',
            content: 'Settings',
            panelID: 'atd-profile-settings',
        },
        {
            id: productSyncTabValue,
            content: 'Product sync',
            panelID: 'atd-profile-product-sync',
        },
    ];

    const filterTabs = [
        {
            id: 'brands',
            content: 'Brands',
            panelID: 'atd-filter-brands',
        },
        {
            id: 'vehicles',
            content: 'Vehicles',
            panelID: 'atd-filter-vehicles',
        },
        {
            id: 'categories',
            content: 'Categories',
            panelID: 'atd-filter-categories',
        },
        {
            id: 'sizes',
            content: 'Sizes',
            panelID: 'atd-filter-sizes',
        },
    ];

    const testProductRows = (testResult?.productSamples || []).map((product: AtdConnectionTestProductSample) => [
        product.atdProductNumber || '-',
        product.manufacturerProductNumber || '-',
        product.brand || '-',
        product.style || '-',
        product.productGroup || '-',
        formatMoney(product.cost),
        `${formatInteger(product.localAvailability)} / ${formatInteger(product.nationwideAvailability)} / ${formatInteger(product.onHandAvailability)}`,
    ]);

    return (
        <>
            <SaveBar id="atd-profile-save-bar" open={hasUnsavedChanges}>
                <button onClick={discardChanges} disabled={saving || loading}>
                    Discard
                </button>
                <button variant="primary" onClick={saveProfile} disabled={saving || loading || testing} loading={saving}>
                    Save
                </button>
            </SaveBar>

            <Modal
                open={testResultModalOpen}
                onClose={() => setTestResultModalOpen(false)}
                title="ATD connection test"
                primaryAction={{
                    content: 'Close',
                    onAction: () => setTestResultModalOpen(false),
                }}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        {testResult ? (
                            <Banner
                                tone={testResult.isSuccess ? 'success' : 'critical'}
                                title={testResult.isSuccess ? 'Connection succeeded' : 'Connection failed'}
                            >
                                <p>{testResult.message || 'No message returned.'}</p>
                            </Banner>
                        ) : null}

                        <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                            <BlockStack gap="050">
                                <Text as="span" tone="subdued">Tested</Text>
                                <Text as="p">{formatDateTime(testResult?.testedAt)}</Text>
                            </BlockStack>
                            <BlockStack gap="050">
                                <Text as="span" tone="subdued">Status</Text>
                                <Text as="p">{testResult?.httpStatusCode ?? '-'}</Text>
                            </BlockStack>
                            <BlockStack gap="050">
                                <Text as="span" tone="subdued">Authorization</Text>
                                <Text as="p">{testResult?.authEndpoint || '-'}</Text>
                            </BlockStack>
                            <BlockStack gap="050">
                                <Text as="span" tone="subdued">Catalog endpoint</Text>
                                <Text as="p">{testResult?.catalogEndpoint || '-'}</Text>
                            </BlockStack>
                        </InlineGrid>

                        <Divider />

                        <Text as="h3" variant="headingSm">Sample products</Text>
                        {testProductRows.length > 0 ? (
                            <DataTable
                                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text']}
                                headings={['ATD #', 'MFG #', 'Brand', 'Style', 'Group', 'Cost', 'Local / National / On hand']}
                                rows={testProductRows}
                            />
                        ) : (
                            <Text as="p" tone="subdued">No sample products were returned.</Text>
                        )}
                    </BlockStack>
                </Modal.Section>
            </Modal>

            <Page
                title="ATD connection"
                backAction={{ content: 'Integrations', onAction: () => navigate('/settings/integrations') }}
                secondaryActions={[
                    {
                        content: 'Test connection',
                        onAction: testProfile,
                        loading: testing,
                        disabled: !profileLoaded || saving || testing,
                    },
                    {
                        content: 'Refresh',
                        onAction: loadProfile,
                        loading,
                        icon: RefreshIcon,
                    },
                ]}
            >
                <Layout>
                    <Layout.Section>
                        <BlockStack gap="400">
                        {error ? (
                            <Banner tone="critical" title="ATD connection error">
                                <p>{error}</p>
                            </Banner>
                        ) : null}

                        {notice ? (
                            <Banner tone="success" title="Saved">
                                <p>{notice}</p>
                            </Banner>
                        ) : null}

                        {!profileLoaded ? (
                            <Card>
                                <BlockStack gap="300">
                                    <Text as="h2" variant="headingMd">Loading ATD connection</Text>
                                    {loading ? (
                                        <SkeletonBodyText lines={8} />
                                    ) : (
                                        <Text as="p" tone="subdued">Refresh the page to load saved ATD settings.</Text>
                                    )}
                                </BlockStack>
                            </Card>
                        ) : (
                            <>
                        <Tabs tabs={profileTabs} selected={selectedProfileTab} onSelect={handleProfileTabSelect} />

                        {selectedProfileTab === 0 ? (
                            <>
                        <Card>
                            <BlockStack gap="300">
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h2" variant="headingMd">Connection</Text>
                                    <Badge tone={isConnectionConfigured ? 'success' : 'attention'}>
                                        {isConnectionConfigured ? 'Configured' : 'Needs setup'}
                                    </Badge>
                                </InlineStack>
                                <Text as="p" tone="subdued">Connect the ATD account and location used for catalog sync and ship-to-home fulfillment.</Text>

                                <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                                    <TextField label="Client ID" autoComplete="off" value={profile.clientId} onChange={(value) => updateProfile('clientId', value)} />
                                    <TextField label="Username" autoComplete="off" value={profile.username} onChange={(value) => updateProfile('username', value)} />
                                    <TextField label="Password" autoComplete="off" type="password" value={profile.password} onChange={(value) => updateProfile('password', value)} />
                                    <TextField label="Secret key" autoComplete="off" type="password" value={profile.secretKey} onChange={(value) => updateProfile('secretKey', value)} />
                                    <TextField label="Account number" autoComplete="off" value={profile.accountNumber} onChange={(value) => updateProfile('accountNumber', value)} />
                                    <TextField label="Location number" autoComplete="off" value={profile.locationNumber} onChange={(value) => updateProfile('locationNumber', value)} />
                                </InlineGrid>

                                <Divider />

                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h3" variant="headingSm">API endpoints</Text>
                                    <Button
                                        onClick={() => setApiEndpointsOpen(open => !open)}
                                        ariaExpanded={apiEndpointsOpen}
                                        ariaControls="atd-api-endpoints"
                                    >
                                        {apiEndpointsOpen ? 'Hide endpoints' : 'Show endpoints'}
                                    </Button>
                                </InlineStack>
                                <Collapsible
                                    open={apiEndpointsOpen}
                                    id="atd-api-endpoints"
                                    transition={{ duration: '250ms', timingFunction: 'ease-in-out' }}
                                    expandOnPrint
                                >
                                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                                        <TextField label="Standard base URL" autoComplete="off" value={profile.standardBaseUrl} onChange={(value) => updateProfile('standardBaseUrl', value)} />
                                        <TextField label="Standard API version" autoComplete="off" value={profile.standardApiVersion} onChange={(value) => updateProfile('standardApiVersion', value)} />
                                        <TextField label="Fitment base URL" autoComplete="off" value={profile.fitmentBaseUrl} onChange={(value) => updateProfile('fitmentBaseUrl', value)} />
                                        <TextField label="Fitment API version" autoComplete="off" value={profile.fitmentApiVersion} onChange={(value) => updateProfile('fitmentApiVersion', value)} />
                                        <TextField label="Ship-to-home base URL" autoComplete="off" value={profile.shipToHomeBaseUrl} onChange={(value) => updateProfile('shipToHomeBaseUrl', value)} />
                                        <TextField label="Ship-to-home API version" autoComplete="off" value={profile.shipToHomeApiVersion} onChange={(value) => updateProfile('shipToHomeApiVersion', value)} />
                                    </InlineGrid>
                                </Collapsible>
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="300">
                                <InlineStack align="space-between" blockAlign="center">
                                    <InlineStack gap="150" blockAlign="center">
                                        <Text as="h2" variant="headingMd">Checkout rates</Text>
                                        <Tooltip content={checkoutRatesHelpText}>
                                            <span aria-label={checkoutRatesHelpText} role="img" style={helpIconStyle} tabIndex={0}>?</span>
                                        </Tooltip>
                                    </InlineStack>
                                    <Badge tone={checkoutRatesReady ? 'success' : 'attention'}>
                                        {checkoutRatesReady ? 'Installed' : 'Not installed'}
                                    </Badge>
                                </InlineStack>

                                {checkoutRateError ? (
                                    <Banner tone="critical" title="Checkout rate setup error">
                                        <p>{checkoutRateError}</p>
                                    </Banner>
                                ) : null}

                                <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
                                    <BlockStack gap="050">
                                        <Text as="span" tone="subdued">Shopify service</Text>
                                        <Text as="p">{checkoutRateStatus?.exists ? 'Found' : 'Missing'}</Text>
                                    </BlockStack>
                                    <BlockStack gap="050">
                                        <Text as="span" tone="subdued">Active</Text>
                                        <Text as="p">{checkoutRateStatus?.active ? 'Yes' : 'No'}</Text>
                                    </BlockStack>
                                    <BlockStack gap="050">
                                        <Text as="span" tone="subdued">Callback</Text>
                                        <Text as="p">{checkoutRateStatus?.callbackUrlMatches ? 'Current' : 'Needs update'}</Text>
                                    </BlockStack>
                                </InlineGrid>

                                <InlineStack gap="200">
                                    <Button
                                        variant="primary"
                                        onClick={ensureCheckoutRates}
                                        loading={checkoutRateSaving}
                                        disabled={!profileLoaded || checkoutRateLoading || checkoutRateSaving || hasUnsavedChanges}
                                    >
                                        Install or repair Shopify service
                                    </Button>
                                    <Button
                                        onClick={loadCheckoutRateStatus}
                                        loading={checkoutRateLoading}
                                        disabled={!profileLoaded || checkoutRateSaving}
                                    >
                                        Refresh status
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="300">
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h2" variant="headingMd">Fulfillment requests</Text>
                                    <Badge tone={profile.autoSubmitFulfillmentOrders ? 'attention' : undefined}>
                                        {profile.autoSubmitFulfillmentOrders ? 'Auto-submit on' : 'Preview first'}
                                    </Badge>
                                </InlineStack>
                                <Text as="p" tone="subdued">Controls what happens after Shopify sends a Request fulfillment event to ATD.</Text>
                                <Checkbox
                                    label="Automatically submit filled ATD previews"
                                    checked={profile.autoSubmitFulfillmentOrders}
                                    onChange={(checked) => updateProfile('autoSubmitFulfillmentOrders', checked)}
                                    helpText="Leave off to review freight, destination, and fulfillment type before sending the ATD place-order request."
                                />
                                <Checkbox
                                    label="Route Shopify pickup requests to Ship-To-Store"
                                    checked={profile.routePickupFulfillmentsToShipToStore}
                                    onChange={(checked) => updateProfile('routePickupFulfillmentsToShipToStore', checked)}
                                    helpText="When Shopify marks the fulfillment order as pickup, default the ATD preview to STS. The order page can still be changed before submit."
                                />
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="300">
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h2" variant="headingMd">Sync</Text>
                                    <Badge tone={syncEnabled ? 'success' : 'attention'}>
                                        {syncEnabled ? 'Enabled' : 'Disabled'}
                                    </Badge>
                                </InlineStack>
                                <Text as="p" tone="subdued">Turn ATD catalog, fitment, and inventory updates on or off for this Shopify store.</Text>
                                <Checkbox
                                    label="Sync ATD catalog, fitment, and inventory data with Shopify"
                                    checked={syncEnabled}
                                    onChange={updateSyncEnabled}
                                />
                                <Checkbox
                                    label="Create new Shopify products as draft"
                                    checked={profile.createProductsAsDraft}
                                    onChange={(checked) => updateProfile('createProductsAsDraft', checked)}
                                    helpText="New products are created in Shopify but stay hidden until activated."
                                />
                                <Checkbox
                                    label="Overwrite existing Shopify product details from ATD"
                                    checked={profile.syncExistingProductDetailsFromAtd}
                                    onChange={(checked) => updateProfile('syncExistingProductDetailsFromAtd', checked)}
                                    helpText="Leave off to preserve merchant edits after the ATD product is created. Inventory still syncs, and discontinued ATD products are archived."
                                />
                                <Checkbox
                                    label="Publish active ATD products to Online Store"
                                    checked={profile.publishProductsToOnlineStore}
                                    onChange={(checked) => updateProfile('publishProductsToOnlineStore', checked)}
                                    helpText="Adds active synced ATD products to the Online Store sales channel. Draft and archived products remain hidden."
                                />
                                <Checkbox
                                    label="Format Shopify product titles as Brand - Product Name - Size"
                                    checked={profile.useBrandProductNameSizeTitleFormat}
                                    onChange={(checked) => updateProfile('useBrandProductNameSizeTitleFormat', checked)}
                                    helpText="Uses the ATD brand, product name, and Size attribute for new ATD products and for existing products when ATD detail overwrites are enabled."
                                />
                                <Checkbox
                                    label="Repair existing ATD Shopify products on next sync"
                                    checked={profile.repairExistingProductsRequested}
                                    onChange={(checked) => updateProfile('repairExistingProductsRequested', checked)}
                                    helpText="Runs one full pass over current eligible ATD products, resends product details, repairs missing or failed ATD media, then turns itself off after the product push finishes."
                                />
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="300">
                                <Text as="h2" variant="headingMd">Product filters</Text>
                                <Text as="p" tone="subdued">Limit which ATD products are eligible to sync by brand, category, or tire size.</Text>
                                <Tabs tabs={filterTabs} selected={selectedFilterTab} onSelect={setSelectedFilterTab} fitted>
                                    {selectedFilterTab === 0 ? (
                                        <FilterCheckboxList
                                            title="Brands"
                                            allLabel="All brands"
                                            emptyText="Brand choices will appear after ATD catalog data has synced."
                                            options={filterOptions.brandNames}
                                            selectedValues={profile.brandNames || []}
                                            onToggle={(value, checked) => toggleFilterValue('brandNames', value, checked, filterOptions.brandNames)}
                                            onSelectAll={() => selectAllFilterValues('brandNames')}
                                        />
                                    ) : null}

                                    {selectedFilterTab === 1 ? (
                                        <VehicleFilterBuilder
                                            options={filterOptions.vehicleMakeOptions}
                                            selectedValues={profile.vehicleYearMakeSelections || []}
                                            onChange={updateVehicleYearMakeSelections}
                                        />
                                    ) : null}

                                    {selectedFilterTab === 2 ? (
                                        <FilterCheckboxList
                                            title="Categories"
                                            allLabel="All categories"
                                            emptyText="Category choices will appear after ATD catalog data has synced."
                                            options={filterOptions.categoryNames}
                                            selectedValues={profile.categoryNames || []}
                                            onToggle={(value, checked) => toggleFilterValue('categoryNames', value, checked, filterOptions.categoryNames)}
                                            onSelectAll={() => selectAllFilterValues('categoryNames')}
                                        />
                                    ) : null}

                                    {selectedFilterTab === 3 ? (
                                        <TextField
                                            label="Tire sizes to sync"
                                            autoComplete="off"
                                            multiline={4}
                                            placeholder={'265/70R17\n275/60R20'}
                                            helpText="Leave blank to include every size. Add one size per line, or separate values with commas."
                                            value={(profile.sizeAttributeValues || []).join('\n')}
                                            onChange={(value) => updateProfile('sizeAttributeValues', parseDelimitedValues(value))}
                                        />
                                    ) : null}
                                </Tabs>
                            </BlockStack>
                        </Card>

                        <Card>
                            <BlockStack gap="300">
                                <InlineStack align="space-between" blockAlign="center">
                                    <Text as="h2" variant="headingMd">Attribute mappings</Text>
                                    <Button onClick={addMapping}>Add mapping</Button>
                                </InlineStack>
                                <Text as="p" tone="subdued">Optional: choose which ATD product details should be written to Shopify product metafields.</Text>
                                {(profile.attributeMetafieldMappings || []).length === 0 ? (
                                    <Text as="p" tone="subdued">No attribute mappings configured.</Text>
                                ) : (
                                    <DataTable
                                        columnContentTypes={['text', 'text', 'text', 'text']}
                                        headings={['ATD product detail', 'Shopify metafield', 'Value type', 'Action']}
                                        rows={(profile.attributeMetafieldMappings || []).map((mapping, index) => [
                                            <Select
                                                key={`atd-attr-${index}`}
                                                label={`ATD product detail ${index + 1}`}
                                                labelHidden
                                                options={attributeOptions}
                                                value={mapping.atdAttributeName || ''}
                                                onChange={(value) => updateMapping(index, 'atdAttributeName', value)}
                                            />,
                                            <TextField
                                                key={`shopify-metafield-${index}`}
                                                label={`Shopify metafield ${index + 1}`}
                                                labelHidden
                                                autoComplete="off"
                                                value={mapping.shopifyProductMetafield || ''}
                                                placeholder="namespace.key"
                                                onChange={(value) => updateMapping(index, 'shopifyProductMetafield', value)}
                                            />,
                                            <Select
                                                key={`metafield-type-${index}`}
                                                label={`Value type ${index + 1}`}
                                                labelHidden
                                                options={metafieldTypeOptions}
                                                value={mapping.shopifyMetafieldType || 'single_line_text_field'}
                                                onChange={(value) => updateMapping(index, 'shopifyMetafieldType', value)}
                                            />,
                                            <Button key={`remove-${index}`} tone="critical" onClick={() => removeMapping(index)}>Remove</Button>,
                                        ])}
                                    />
                                )}
                            </BlockStack>
                        </Card>
                            </>
                        ) : (
                            <AtdProductSyncPanel />
                        )}
                            </>
                        )}
                        </BlockStack>
                    </Layout.Section>
                </Layout>
            </Page>
        </>
    );
};

export default AtdProfilePage;
